import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CreditCard, Truck, Check, Plus, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [{ label: "Address", icon: MapPin }, { label: "Delivery", icon: Truck }, { label: "Payment", icon: CreditCard }, { label: "Confirm", icon: Check }];

export default function Checkout() {
  const { user, fetchCartCount } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deliverySlot, setDeliverySlot] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [newAddr, setNewAddr] = useState({ name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", is_default: true });

  useEffect(() => {
    axios.get(`${API}/cart`, { withCredentials: true }).then(r => setCart(r.data));
    axios.get(`${API}/user/addresses`, { withCredentials: true }).then(r => {
      setAddresses(r.data);
      const def = r.data.find(a => a.is_default);
      if (def) setSelectedAddress(def.address_id);
      else if (r.data.length > 0) setSelectedAddress(r.data[0].address_id);
    });
  }, []);

  const saveAddress = async () => {
    try {
      const res = await axios.post(`${API}/user/addresses`, newAddr, { withCredentials: true });
      setAddresses([...addresses, res.data]);
      setSelectedAddress(res.data.address_id);
      setShowAddForm(false);
      toast.success("Address saved!");
    } catch (err) { toast.error("Failed to save address"); }
  };

  const placeOrder = async () => {
    if (!selectedAddress) { toast.error("Please select delivery address"); return; }
    const addr = addresses.find(a => a.address_id === selectedAddress);
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/orders`, { delivery_address: addr, payment_method: paymentMethod }, { withCredentials: true });
      fetchCartCount();
      toast.success("Order placed successfully!");
      navigate(`/order-confirmation/${res.data.order_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to place order"); } finally { setPlacing(false); }
  };

  const subtotal = cart.total;
  const delivery = subtotal >= 499 ? 0 : (deliverySlot === "express" ? 99 : 49);
  const tax = Math.round(subtotal * 0.05 * 100) / 100;
  const total = (subtotal + delivery + tax).toFixed(2);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="checkout-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Checkout</h1>

      {/* Steps */}
      <div className="flex items-center justify-between mb-8 bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${i <= step ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
              {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i <= step ? 'text-cyan-600' : 'text-slate-400'}`}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 mx-2" />}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Step 0: Address */}
          {step === 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="checkout-address-step">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Delivery Address</h2>
              {addresses.length > 0 && (
                <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress} className="space-y-3 mb-4">
                  {addresses.map(addr => (
                    <div key={addr.address_id} className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-colors cursor-pointer ${selectedAddress === addr.address_id ? 'border-cyan-500 bg-cyan-50' : 'border-slate-100'}`}>
                      <RadioGroupItem value={addr.address_id} id={addr.address_id} />
                      <label htmlFor={addr.address_id} className="cursor-pointer flex-1">
                        <p className="text-sm font-semibold text-slate-800">{addr.name} - {addr.phone}</p>
                        <p className="text-xs text-slate-500">{addr.address_line1}, {addr.address_line2 && `${addr.address_line2}, `}{addr.city}, {addr.state} - {addr.pincode}</p>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              )}
              {showAddForm ? (
                <div className="space-y-3 border border-slate-200 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Name</Label><Input value={newAddr.name} onChange={e => setNewAddr({...newAddr, name: e.target.value})} className="rounded-xl mt-1" data-testid="addr-name" /></div>
                    <div><Label className="text-xs">Phone</Label><Input value={newAddr.phone} onChange={e => setNewAddr({...newAddr, phone: e.target.value})} className="rounded-xl mt-1" data-testid="addr-phone" /></div>
                  </div>
                  <div><Label className="text-xs">Address Line 1</Label><Input value={newAddr.address_line1} onChange={e => setNewAddr({...newAddr, address_line1: e.target.value})} className="rounded-xl mt-1" data-testid="addr-line1" /></div>
                  <div><Label className="text-xs">Address Line 2</Label><Input value={newAddr.address_line2} onChange={e => setNewAddr({...newAddr, address_line2: e.target.value})} className="rounded-xl mt-1" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">City</Label><Input value={newAddr.city} onChange={e => setNewAddr({...newAddr, city: e.target.value})} className="rounded-xl mt-1" data-testid="addr-city" /></div>
                    <div><Label className="text-xs">State</Label><Input value={newAddr.state} onChange={e => setNewAddr({...newAddr, state: e.target.value})} className="rounded-xl mt-1" data-testid="addr-state" /></div>
                    <div><Label className="text-xs">Pincode</Label><Input value={newAddr.pincode} onChange={e => setNewAddr({...newAddr, pincode: e.target.value})} className="rounded-xl mt-1" data-testid="addr-pincode" /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={saveAddress} size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-addr-btn">Save Address</Button>
                    <Button onClick={() => setShowAddForm(false)} variant="ghost" size="sm" className="rounded-full">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setShowAddForm(true)} variant="outline" className="rounded-full border-dashed border-cyan-300 text-cyan-600" data-testid="add-addr-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add New Address
                </Button>
              )}
              <Button onClick={() => { if (!selectedAddress) { toast.error("Select an address"); return; } setStep(1); }} className="mt-4 rounded-full bg-cyan-600 hover:bg-cyan-700 w-full" data-testid="next-step-btn">Continue</Button>
            </div>
          )}

          {/* Step 1: Delivery */}
          {step === 1 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="checkout-delivery-step">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Delivery Slot</h2>
              <RadioGroup value={deliverySlot} onValueChange={setDeliverySlot} className="space-y-3">
                {[{ value: "express", label: "Express (2 Hours)", price: "Rs.99", desc: "Get it within 2 hours" }, { value: "standard", label: "Standard (24 Hours)", price: subtotal >= 499 ? "FREE" : "Rs.49", desc: "Delivery within 24 hours" }, { value: "scheduled", label: "Scheduled", price: subtotal >= 499 ? "FREE" : "Rs.49", desc: "Choose your preferred date" }].map(s => (
                  <div key={s.value} className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer ${deliverySlot === s.value ? 'border-cyan-500 bg-cyan-50' : 'border-slate-100'}`} onClick={() => setDeliverySlot(s.value)}>
                    <div className="flex items-center gap-3"><RadioGroupItem value={s.value} /><div><p className="text-sm font-medium text-slate-800">{s.label}</p><p className="text-xs text-slate-500">{s.desc}</p></div></div>
                    <span className="text-sm font-semibold text-cyan-600">{s.price}</span>
                  </div>
                ))}
              </RadioGroup>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(0)} variant="outline" className="rounded-full">Back</Button>
                <Button onClick={() => setStep(2)} className="flex-1 rounded-full bg-cyan-600 hover:bg-cyan-700">Continue</Button>
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="checkout-payment-step">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Payment Method</h2>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                {[{ value: "cod", label: "Cash on Delivery", desc: "Pay when you receive" }, { value: "upi", label: "UPI", desc: "GPay, PhonePe, Paytm" }, { value: "card", label: "Credit/Debit Card", desc: "Visa, Mastercard, RuPay" }, { value: "netbanking", label: "Net Banking", desc: "All major banks" }].map(m => (
                  <div key={m.value} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${paymentMethod === m.value ? 'border-cyan-500 bg-cyan-50' : 'border-slate-100'}`} onClick={() => setPaymentMethod(m.value)}>
                    <RadioGroupItem value={m.value} /><div><p className="text-sm font-medium text-slate-800">{m.label}</p><p className="text-xs text-slate-500">{m.desc}</p></div>
                  </div>
                ))}
              </RadioGroup>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(1)} variant="outline" className="rounded-full">Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 rounded-full bg-cyan-600 hover:bg-cyan-700">Review Order</Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="checkout-confirm-step">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Order Review</h2>
              <div className="space-y-4">
                {cart.items.map(item => (
                  <div key={item.item_id} className="flex items-center gap-3">
                    <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover bg-slate-50" />
                    <div className="flex-1"><p className="text-sm font-medium text-slate-800">{item.name}</p><p className="text-xs text-slate-500">Qty: {item.quantity}</p></div>
                    <span className="text-sm font-semibold">Rs.{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-sky-50 rounded-xl text-sm text-slate-600">
                <p><strong>Delivery:</strong> {addresses.find(a => a.address_id === selectedAddress)?.address_line1}</p>
                <p><strong>Payment:</strong> {paymentMethod.toUpperCase()}</p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => setStep(2)} variant="outline" className="rounded-full">Back</Button>
                <Button onClick={placeOrder} disabled={placing} className="flex-1 rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="place-order-btn">
                  {placing ? <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : `Place Order - Rs.${total}`}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Summary */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-fit sticky top-24">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal ({cart.items.length} items)</span><span>Rs.{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Delivery</span><span>{delivery === 0 ? <span className="text-emerald-600">FREE</span> : `Rs.${delivery}`}</span></div>
            <div className="flex justify-between text-slate-600"><span>GST (5%)</span><span>Rs.{tax.toFixed(2)}</span></div>
            <div className="border-t pt-2 flex justify-between font-bold text-slate-800 text-base"><span>Total</span><span>Rs.{total}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
